/**
 * XStateConnector — full state machine resource via XState v5.
 * Not mountable. Provides guarded transitions, entry/exit actions,
 * context, and serializable snapshots.
 *
 * Requires `xstate` (v5+) as an optional peer dependency.
 *
 * `options.definitionPath` is declaration-supplied, so a machine file under the
 * workspace is plausible configuration — which puts it on the rclone FUSE mount
 * inside the agent container. The load is therefore async and deadline-bounded:
 * a synchronous read against a wedged mount parks the whole event loop in an
 * uninterruptible kernel wait (issue #454).
 */

import { readFile } from "node:fs/promises";
import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorEntry,
  ConnectorHandle,
  ListOptions,
  OperationDescriptor,
  SearchResult,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";
import { FsDeadlineError, withFsDeadline } from "@skaile/workspaces/core";

interface XStateState {
  actor: any; // XState Actor
  machine: any; // XState Machine (for introspection)
  definition: any; // Raw definition object
  history: Array<{ event: string; from: string; to: string; at: string }>;
  onChange?: (snapshot: { value: any; context: any }) => void;
  actions: Record<string, (...args: any[]) => void>;
  guards: Record<string, (...args: any[]) => boolean>;
}

/**
 * Connector for XState v5 state machines — drives guarded transitions, tracks history, and exposes
 * the current state and context to agents. Machine definitions can be provided inline or loaded
 * from a JSON/YAML file. Requires the `xstate` optional peer dependency.
 * @docLink packages/factory-assets/concepts#xstate-connector
 */
export class XStateConnector extends AbstractConnector {
  readonly name = "xstate";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this._read(handle, path),
      list: (handle, path, options) => this._list(handle, path, options),
      search: (handle, query) => this._search(handle, query),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
    };
  }

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    this.log.info("connect", { id: declaration.id });
    const xstatePkg = "xstate";
    const xstate = (await import(/* @vite-ignore */ xstatePkg)) as any;
    const { createMachine, createActor } = xstate;

    // Load machine definition
    let definition = declaration.options?.machine as Record<string, unknown> | undefined;
    if (!definition && declaration.options?.definitionPath) {
      const defPath = String(declaration.options.definitionPath);
      // Read directly instead of probing first: the exists check could not tell
      // "absent" from "mount not answering", and on a hung mount it never
      // returned at all. A deadline miss keeps its own message.
      let raw: string;
      try {
        raw = await withFsDeadline(`readFile ${defPath}`, () => readFile(defPath, "utf-8"));
      } catch (err) {
        if (err instanceof FsDeadlineError) throw err;
        throw new Error(`Machine definition not found: ${defPath}`);
      }
      if (defPath.endsWith(".json")) {
        definition = JSON.parse(raw);
      } else {
        const { parse } = (await import(/* @vite-ignore */ "yaml")) as any;
        definition = parse(raw);
      }
    }

    if (!definition)
      throw new Error("XStateAdapter requires options.machine or options.definitionPath");

    const state: XStateState = {
      actor: null,
      machine: null,
      definition,
      history: [],
      actions: {},
      guards: {},
    };

    // Build the machine — actions and guards are proxied so they can be
    // registered after connection (by the runner or Nuxt server)
    const actionProxy: Record<string, (...args: any[]) => void> = {};
    const guardProxy: Record<string, (...args: any[]) => boolean> = {};

    // Collect action/guard names from definition
    const actionNames = this.collectNames(definition, "actions");
    const guardNames = this.collectNames(definition, "guard");

    for (const name of actionNames) {
      actionProxy[name] = (...args: any[]) => {
        if (state.actions[name]) state.actions[name](...args);
      };
    }
    for (const name of guardNames) {
      guardProxy[name] = (...args: any[]) => {
        return state.guards[name] ? state.guards[name](...args) : true;
      };
    }

    const machineConfig = {
      ...definition,
      // Ensure context is an object
      context: definition.context ?? {},
    };

    const machine = createMachine(machineConfig, {
      actions: actionProxy,
      guards: guardProxy,
    });

    const actor = createActor(machine);

    // Track state changes
    actor.subscribe((snapshot: any) => {
      state.onChange?.({ value: snapshot.value, context: snapshot.context });
    });

    actor.start();

    state.actor = actor;
    state.machine = machine;

    return this.makeHandle(declaration, state);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    const s = handle.state as XStateState;
    try {
      s.actor?.stop();
    } finally {
      handle.status = "disconnected";
    }
  }

  private s(handle: ConnectorHandle): XStateState {
    return handle.state as XStateState;
  }

  /**
   * Register action implementations. Call after connect().
   */
  registerActions(
    handle: ConnectorHandle,
    actions: Record<string, (...args: any[]) => void>,
  ): void {
    Object.assign(this.s(handle).actions, actions);
  }

  /**
   * Register guard implementations. Call after connect().
   */
  registerGuards(
    handle: ConnectorHandle,
    guards: Record<string, (...args: any[]) => boolean>,
  ): void {
    Object.assign(this.s(handle).guards, guards);
  }

  /**
   * Register a callback fired on every state change.
   */
  onStateChange(
    handle: ConnectorHandle,
    cb: (snapshot: { value: any; context: any }) => void,
  ): void {
    this.s(handle).onChange = cb;
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const snapshot = this.s(handle).actor.getSnapshot();
    if (path === "" || path === "state") {
      return { data: JSON.stringify(snapshot.value), contentType: "application/json" };
    }
    if (path === "context") {
      return { data: JSON.stringify(snapshot.context, null, 2), contentType: "application/json" };
    }
    // Read a specific context key
    const value = snapshot.context?.[path];
    if (value === undefined) return null;
    return { data: JSON.stringify(value), contentType: "application/json" };
  }

  private async _list(
    handle: ConnectorHandle,
    _path?: string,
    _options?: ListOptions,
  ): Promise<ConnectorEntry[]> {
    const snapshot = this.s(handle).actor.getSnapshot();
    const entries: ConnectorEntry[] = [
      { name: "state", path: "state", type: "key" },
      { name: "context", path: "context", type: "key" },
    ];
    if (snapshot.context && typeof snapshot.context === "object") {
      for (const key of Object.keys(snapshot.context)) {
        entries.push({ name: key, path: `context/${key}`, type: "key" });
      }
    }
    return entries;
  }

  private async _search(handle: ConnectorHandle, query: string): Promise<SearchResult[]> {
    const snapshot = this.s(handle).actor.getSnapshot();
    const full = JSON.stringify({ value: snapshot.value, context: snapshot.context });
    if (full.includes(query)) {
      return [{ path: "state", snippet: full.slice(0, 200) }];
    }
    return [];
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "get_state",
        description: "Get the current state value, context, and list of available transitions.",
        accessLevel: "read",
        args: [],
      },
      {
        name: "get_definition",
        description: "Get the machine definition (states, transitions, guards).",
        accessLevel: "read",
        args: [],
      },
      {
        name: "history",
        description: "Get the transition history log.",
        accessLevel: "read",
        args: [
          {
            name: "limit",
            type: "number",
            required: false,
            description: "Max entries (default: 50)",
          },
        ],
      },
      {
        name: "can",
        description: "Check if a specific event/transition is currently allowed.",
        accessLevel: "read",
        args: [
          { name: "event", type: "string", required: true, description: "Event type to check" },
        ],
      },
    ];

    if (handle.access === "read-write") {
      ops.push({
        name: "send",
        description: "Send an event to the state machine, triggering a transition if valid.",
        accessLevel: "write",
        args: [
          {
            name: "event",
            type: "string",
            required: true,
            description: "Event type (e.g., SUBMIT, APPROVE)",
          },
          { name: "data", type: "json", required: false, description: "Event payload data (JSON)" },
        ],
        examples: ["send --event APPROVE", 'send --event SUBMIT --data \'{"reviewer":"alice"}\''],
      });
    }

    return ops;
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const s = this.s(handle);

    switch (operation) {
      case "get_state": {
        const snapshot = s.actor.getSnapshot();
        const nextEvents = this.getAvailableEvents(handle);
        return JSON.stringify(
          {
            value: snapshot.value,
            context: snapshot.context,
            availableEvents: nextEvents,
          },
          null,
          2,
        );
      }

      case "get_definition":
        return JSON.stringify(s.definition, null, 2);

      case "history": {
        const limit = Number(args.limit ?? 50);
        return JSON.stringify(s.history.slice(-limit), null, 2);
      }

      case "can": {
        const event = String(args.event ?? "");
        const snapshot = s.actor.getSnapshot();
        const can =
          snapshot.can?.({ type: event }) ?? this.getAvailableEvents(handle).includes(event);
        return JSON.stringify({ event, allowed: can });
      }

      case "send": {
        const eventType = String(args.event ?? args._positional ?? "");
        if (!eventType) return "Error: event argument is required";

        const snapshot = s.actor.getSnapshot();
        const fromState =
          typeof snapshot.value === "string" ? snapshot.value : JSON.stringify(snapshot.value);

        let eventPayload: Record<string, unknown> = { type: eventType };
        if (args.data) {
          const data = typeof args.data === "string" ? JSON.parse(args.data) : args.data;
          eventPayload = { type: eventType, ...data };
        }

        s.actor.send(eventPayload);

        const newSnapshot = s.actor.getSnapshot();
        const toState =
          typeof newSnapshot.value === "string"
            ? newSnapshot.value
            : JSON.stringify(newSnapshot.value);

        const transitioned = fromState !== toState;
        s.history.push({
          event: eventType,
          from: fromState,
          to: toState,
          at: new Date().toISOString(),
        });

        // Cap history
        if (s.history.length > 500) s.history = s.history.slice(-500);

        if (transitioned) {
          this.emitChange?.({ path: "state", action: "edit", source: "operation" });
        }

        return JSON.stringify(
          {
            transitioned,
            from: fromState,
            to: toState,
            context: newSnapshot.context,
            availableEvents: this.getAvailableEvents(handle),
          },
          null,
          2,
        );
      }

      default:
        throw new Error(`XStateAdapter has no operation: ${operation}`);
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private getAvailableEvents(handle: ConnectorHandle): string[] {
    const snapshot = this.s(handle).actor.getSnapshot();
    const currentState = typeof snapshot.value === "string" ? snapshot.value : null;
    if (!currentState) return [];
    const stateDef = this.s(handle).definition?.states?.[currentState];
    if (!stateDef?.on) return [];
    return Object.keys(stateDef.on);
  }

  /** Collect all named actions or guards from a machine definition tree. */
  private collectNames(def: any, key: "actions" | "guard"): string[] {
    const names = new Set<string>();
    const walk = (obj: any) => {
      if (!obj || typeof obj !== "object") return;
      if (key === "actions") {
        for (const field of ["actions", "entry", "exit"]) {
          const val = obj[field];
          if (typeof val === "string") names.add(val);
          if (Array.isArray(val))
            val.forEach((v: any) => {
              if (typeof v === "string") names.add(v);
            });
        }
      }
      if (key === "guard" && typeof obj.guard === "string") {
        names.add(obj.guard);
      }
      for (const v of Object.values(obj)) {
        if (v && typeof v === "object") walk(v);
      }
    };
    walk(def);
    return [...names];
  }
}

/**
 * Creates a new XStateConnector instance.
 * @returns Configured XStateConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#xstate-connector-factory
 */
export function createConnector(): XStateConnector {
  return new XStateConnector();
}
